// src/pages/settings/SettingsPage.tsx

import React from 'react';
import { Link } from 'react-router-dom';
import {
  User,
  Building2,
  CreditCard,
  Bell,
  Settings,
  ChevronRight,
  Menu,
  Monitor,
  Bot,
} from 'lucide-react';
import { useAuth, usePermissions } from '../../contexts/AuthContext';
import { useChatLayout } from '../../components/layout/ChatLayout';
import WorkspacePill from '../../components/layout/WorkspacePill';

const SettingsPage: React.FC = () => {
  const { user, workspace, workspaceRole } = useAuth();
  const { canManageOrganization, canManageUsers } = usePermissions();
  const roleLabel =
    workspaceRole === 'workspace-owner' ? 'Owner'
    : workspaceRole === 'workspace-admin' ? 'Admin'
    : workspaceRole === 'workspace-member' ? 'Member'
    : null;
  const { isMobile, toggleSidebar } = useChatLayout();

  const settingsSections = [
    {
      title: 'Account',
      description: 'Manage your personal account settings',
      items: [
        {
          icon: User,
          title: 'Profile',
          description: 'Personal info, password, two-factor authentication, and preferences',
          href: '/settings/profile',
          available: true,
        },
        {
          icon: Bell,
          title: 'Notifications',
          description: 'Configure email alerts and notification preferences',
          href: '/settings/notifications',
          available: true,
        },
        {
          icon: Monitor,
          title: 'Active Sessions',
          description: 'View and manage your active login sessions across devices',
          href: '/settings/sessions',
          available: true,
        },
        {
          icon: Bot,
          title: 'External AI Access',
          description: 'Approve or revoke AI assistants (Claude.ai, ChatGPT) that connect to your data',
          href: '/settings/external-ai-access',
          available: true,
        },
      ],
    },
    {
      title: 'Workspace',
      description: 'Manage your workspace, team, and billing',
      items: [
        {
          icon: Building2,
          title: 'Workspace',
          description: 'Name, plan, and the businesses inside your workspace',
          href: '/settings/workspace',
          available: canManageOrganization(),
        },
        {
          icon: User,
          title: 'Team Members',
          description: 'Invite users, manage roles, and team permissions',
          href: '/settings/team',
          available: canManageUsers(),
        },
        {
          icon: CreditCard,
          title: 'Billing & Subscription',
          description: 'Manage your subscription, billing details, and invoices',
          href: '/settings/billing',
          available: canManageOrganization(),
        },
      ],
    },
  ];

  return (
    <div className="flex flex-col min-h-screen px-4 py-8 sm:py-12 relative">
      {isMobile && (
        <button onClick={toggleSidebar} className="absolute top-4 left-4 p-2 rounded-lg text-muted-foreground hover:bg-accent transition-colors" aria-label="Open sidebar">
          <Menu className="w-5 h-5" />
        </button>
      )}
      <div className="w-full max-w-4xl mx-auto space-y-8">
      {/* Header — title left, workspace selector top-right (matches /home) */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <Settings className="w-8 h-8 text-primary" />
          <div>
            <h1 className="text-3xl font-bold text-foreground">
              Settings
            </h1>
            <p className="text-muted-foreground mt-2">
              Manage your account, organization, and application preferences
            </p>
          </div>
        </div>
        <WorkspacePill />
      </div>

      {/* User Info Card */}
      <div className="rounded-xl bg-muted border border-border p-6">
        <div className="flex items-center">
          <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center">
            <User className="w-8 h-8 text-white" />
          </div>
          <div className="ml-6">
            <h2 className="text-xl font-semibold text-foreground">
              {user?.firstName} {user?.lastName}
            </h2>
            <p className="text-muted-foreground">
              {user?.email}
            </p>
            <div className="flex items-center mt-2">
              {roleLabel && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-accent text-primary">
                  {roleLabel}
                </span>
              )}
              <span className="ml-2 text-sm text-muted-foreground">
                in {workspace?.name}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Settings Sections */}
      {settingsSections.map((section) => (
        <div key={section.title} className="space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-foreground">
              {section.title}
            </h2>
            <p className="text-sm text-muted-foreground">
              {section.description}
            </p>
          </div>

          <div className="rounded-xl bg-muted border border-border divide-y divide-border">
            {section.items
              .filter((item) => item.available)
              .map((item) => (
                <div key={item.title}>
                  {item.href ? (
                    <Link
                      to={item.href}
                      className="flex items-center justify-between p-6 hover:bg-accent/50 dark:hover:bg-white/[0.03] transition-colors"
                    >
                      <div className="flex items-center">
                        <div className="p-2 rounded-lg bg-muted">
                          <item.icon className="w-5 h-5 text-muted-foreground" />
                        </div>
                        <div className="ml-4">
                          <h3 className="text-sm font-medium text-foreground">
                            {item.title}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            {item.description}
                          </p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-muted-foreground" />
                    </Link>
                  ) : (
                    <div className="flex items-center justify-between p-6 opacity-50 cursor-not-allowed">
                      <div className="flex items-center">
                        <div className="p-2 bg-muted rounded-lg">
                          <item.icon className="w-5 h-5 text-muted-foreground" />
                        </div>
                        <div className="ml-4">
                          <h3 className="text-sm font-medium text-foreground">
                            {item.title}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            {item.description}
                          </p>
                        </div>
                      </div>
                      <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
                        Coming Soon
                      </span>
                    </div>
                  )}
                </div>
              ))}
          </div>
        </div>
      ))}

      </div>
    </div>
  );
};

export default SettingsPage;
